import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Routes, Route, useParams, useNavigate } from 'react-router-dom';
import { Alert, H1, Breadcrumbs, Loader, Tabs, Toggle } from '@itds/elements/index.js';
import { useDispatch, useSelector } from 'react-redux';

import Details from './Details';
import VersionForm from './VersionForm';
import Versions from './Versions';
import Notes from './Notes';
import {
  fetchFormDetails,
  toggleFormActiveStatus,
  setSuccessMessage,
  clearMessages
} from '@redux/carrierSlices/formSlice';

const Show = ({ carrier }) => {
  const navigate = useNavigate();
  const params = useParams();
  const dispatch = useDispatch();

  const { currentForm, productGroups, formTags, detailsLoading, detailsError, successMessage } =
    useSelector(state => state.forms);

  const [version, setVersion] = useState(null);

  useEffect(() => {
    if (carrier?.id && params.form_id) {
      dispatch(
        fetchFormDetails({
          carrierId: carrier.id,
          formId: params.form_id
        })
      );
    }

    return () => {
      dispatch(clearMessages());
    };
  }, [carrier?.id, params.form_id, dispatch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      dispatch(clearMessages());
    }, 2000);

    return () => clearTimeout(timer);
  }, [detailsError, successMessage, dispatch]);

  const handleToggleStatus = () => {
    if (!currentForm) return;
    dispatch(
      toggleFormActiveStatus({
        formId: currentForm.id,
        isActive: !currentForm.is_active
      })
    );
  };

  const handleSave = action => {
    setVersion(null);
    dispatch(
      setSuccessMessage(
        (() => {
          if (action === 'add') {
            return 'Form version successfully added';
          } else if (action === 'delete') {
            return 'Form version successfully deleted';
          } else {
            return 'Form successfully changed';
          }
        })()
      )
    );
    dispatch(
      fetchFormDetails({
        carrierId: carrier.id,
        formId: params.form_id
      })
    );
  };

  const props = {
    form: currentForm,
    form_tags: formTags,
    product_groups: productGroups,
    setVersion,
    loadForm: () =>
      dispatch(
        fetchFormDetails({
          carrierId: carrier.id,
          formId: params.form_id
        })
      )
  };

  if (detailsLoading && !currentForm) {
    return <Loader />;
  }

  if (!currentForm) {
    return <Alert type='error'>Form not found</Alert>;
  }

  return (
    <>
      {successMessage && <Alert type='success'>{successMessage}</Alert>}
      {detailsError && <Alert type='error'>{detailsError}</Alert>}

      <VersionForm
        form={{
          carrier_id: carrier.id,
          form_id: currentForm.id,
          ...version
        }}
        formTags={formTags}
        onClose={() => setVersion(null)}
        onSave={handleSave}
        productGroups={productGroups}
        show={!!version}
      />

      <div style={{ display: 'flex' }}>
        <div>
          <H1>{currentForm.name}</H1>
          <Breadcrumbs
            crumbs={[
              { label: 'All Forms', onClick: () => navigate(`/carriers/${carrier.id}/forms`) }
            ]}
            current={currentForm.name}
          />
        </div>
        <div style={{ marginLeft: 'auto' }}>
          <Toggle active={currentForm.is_active} onClick={handleToggleStatus} />
        </div>
      </div>

      <div style={{ display: 'flex' }}>
        <Tabs
          tabs={[
            {
              label: 'Details',
              to: '',
              isActive:
                !window.location.hash.includes('versions') &&
                !window.location.hash.includes('notes')
            },
            {
              label: 'Versions',
              to: 'versions',
              isActive: window.location.hash.includes('versions')
            },
            {
              label: 'Notes',
              to: 'notes',
              isActive: window.location.hash.includes('notes')
            }
          ]}
          vertical={true}
        />
        <div style={{ marginLeft: 40, flex: 1 }}>
          <Routes>
            <Route element={<Details {...props} />} index={true} />
            <Route element={<Versions {...props} />} path='versions' />
            <Route element={<Notes {...props} />} path='notes' />
          </Routes>
        </div>
      </div>
    </>
  );
};

Show.propTypes = {
  carrier: PropTypes.object
};

export default Show;
